// AI Analysis Call Detail - Individual Call Review
// Location: fongo-dashboard/src/components/AICallDetail.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

interface CallAnalysis {
  // Call metadata
  call_id: string;
  customer_name: string;
  caller_name: string;
  channel_type: string;
  issue_category: string;
  resolution_status: string;
  duration_seconds: number;
  created_at: string;
  transcript: string;
  
  // Grades
  overall_grade: number;
  grade_letter: string;
  authentication_grade: number;
  authentication_status?: string;
  issue_identification_grade: number;
  troubleshooting_grade: number;
  escalation_grade: number;
  communication_grade: number;
  resolution_grade: number;
  
  // Issues
  critical_issues: Array<{
    type: string;
    description: string;
    timestamp_in_call?: string;
    quote?: string;
    impact: string;
  }>;
  moderate_issues: Array<{
    type: string;
    description: string;
    timestamp_in_call?: string;
    quote?: string;
    impact: string;
  }>;
  positive_highlights: Array<{
    type: string;
    description: string;
    timestamp_in_call?: string;
    quote?: string;
  }>;
  
  // Recommendations
  recommendations: string[];
  similar_call_ids: string[];
  
  // Review
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
}

export default function AICallDetail() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [call, setCall] = useState<CallAnalysis | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (callId) {
      fetchCallAnalysis();
    }
  }, [callId]);

  const fetchCallAnalysis = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/ai-analysis/call/${callId}`);
      if (response.ok) {
        const data = await response.json();
        setCall(data);
        setReviewNotes(data.review_notes || '');
      }
    } catch (error) {
      console.error('Error fetching call analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReview = async () => {
    if (!callId) return;
    
    try {
      setSaving(true);
      const response = await fetch(`/api/ai-analysis/call/${callId}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: reviewNotes })
      });
      
      if (response.ok) {
        await fetchCallAnalysis(); // Refresh data
      }
    } catch (error) {
      console.error('Error saving review:', error);
    } finally {
      setSaving(false);
    }
  };

  const getGradeColor = (grade: number) => {
    if (grade >= 93) return 'success';
    if (grade >= 85) return 'info';
    if (grade >= 70) return 'warning';
    return 'error';
  };

  const getAuthStatusColor = (status?: string) => {
    if (!status) return 'default';
    
    if (status === 'Customer Verified') return 'success';
    if (status === 'Customer Not Found') return 'info';
    if (status === 'Credentials Mismatch') return 'warning';
    if (status === 'Authentication Waived - No Sensitive Data') return 'info';
    if (status === 'Escalated for Human Verification') return 'info';
    if (status === 'Authentication Bypass' || status === 'Authentication Not Attempted') return 'error';
    
    return 'default';
  };

  const getAuthStatusIcon = (status?: string) => {
    if (!status) return null;
    
    if (status === 'Customer Verified') return <CheckCircleIcon />;
    if (status === 'Customer Not Found' || status === 'Credentials Mismatch') return <WarningIcon />;
    if (status === 'Authentication Waived - No Sensitive Data' || status === 'Escalated for Human Verification') return <CheckCircleIcon />;
    if (status === 'Authentication Bypass' || status === 'Authentication Not Attempted') return <ErrorIcon />;
    
    return null;
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  if (loading || !call) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/ai-analysis')}
        sx={{ mb: 3 }}
      >
        Back to Analysis List
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              {call.channel_type === 'VOICE' ? <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> : <ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
              Call Analysis: {call.customer_name}
            </Typography>
            <Typography variant="body2">
              {new Date(call.created_at).toLocaleString()} • {formatDuration(call.duration_seconds)} • {call.issue_category}
            </Typography>
            <Typography variant="caption">
              Call ID: {call.call_id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} textAlign="right">
            <Typography variant="h1" sx={{ fontSize: '80px', fontWeight: 'bold' }}>
              {call.grade_letter}
            </Typography>
            <Typography variant="h6">
              {call.overall_grade?.toFixed(1)}%
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Grades */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Performance Breakdown
            </Typography>
            <Divider sx={{ my: 2 }} />

            {[
              { label: 'Authentication', grade: call.authentication_grade, status: call.authentication_status },
              { label: 'Issue Identification', grade: call.issue_identification_grade },
              { label: 'Troubleshooting', grade: call.troubleshooting_grade },
              { label: 'Escalation Handling', grade: call.escalation_grade },
              { label: 'Communication', grade: call.communication_grade },
              { label: 'Resolution', grade: call.resolution_grade },
            ].map((item, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{item.label}</Typography>
                    {item.status && (
                      <Chip
                        label={item.status}
                        size="small"
                        color={getAuthStatusColor(item.status) as any}
                        icon={getAuthStatusIcon(item.status) || undefined}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {item.grade ? `${item.grade}%` : 'N/A'}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={item.grade || 0} 
                  color={getGradeColor(item.grade || 0)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ))}
          </Paper>

          {/* Recommendations */}
          {call.recommendations && call.recommendations.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Recommendations for Improvement
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List>
                {call.recommendations.map((rec, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={rec} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Grid>

        {/* Right Column - Issues */}
        <Grid item xs={12} md={6}>
          {/* Critical Issues */}
          {call.critical_issues && call.critical_issues.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #f44336' }}>
              <Typography variant="h6" gutterBottom color="error">
                <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Critical Issues ({call.critical_issues.length})
              </Typography>
              <Divider sx={{ my: 2 }} />
              {call.critical_issues.map((issue, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="error">
                    {issue.type}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {issue.description}
                  </Typography>
                  {issue.quote && (
                    <Box sx={{ mt: 1, p: 1, backgroundColor: 'white', borderRadius: 1, borderLeft: '3px solid #f44336' }}>
                      <Typography variant="caption" fontStyle="italic">
                        "{issue.quote}"
                      </Typography>
                    </Box>
                  )}
                  {issue.timestamp_in_call && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      At: {issue.timestamp_in_call}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          )}

          {/* Moderate Issues */}
          {call.moderate_issues && call.moderate_issues.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #ff9800' }}>
              <Typography variant="h6" gutterBottom color="warning.main">
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Moderate Issues ({call.moderate_issues.length})
              </Typography>
              <Divider sx={{ my: 2 }} />
              {call.moderate_issues.map((issue, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                    {issue.type}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {issue.description}
                  </Typography>
                  {issue.quote && (
                    <Box sx={{ mt: 1, p: 1, backgroundColor: 'white', borderRadius: 1, borderLeft: '3px solid #ff9800' }}>
                      <Typography variant="caption" fontStyle="italic">
                        "{issue.quote}"
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          )}

          {/* Positive Highlights */}
          {call.positive_highlights && call.positive_highlights.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #4caf50' }}>
              <Typography variant="h6" gutterBottom color="success.main">
                <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Positive Highlights ({call.positive_highlights.length})
              </Typography>
              <Divider sx={{ my: 2 }} />
              {call.positive_highlights.map((highlight, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                    {highlight.type}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {highlight.description}
                  </Typography>
                  {highlight.quote && (
                    <Box sx={{ mt: 1, p: 1, backgroundColor: 'white', borderRadius: 1, borderLeft: '3px solid #4caf50' }}>
                      <Typography variant="caption" fontStyle="italic">
                        "{highlight.quote}"
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          )}

          {/* No Issues */}
          {(!call.critical_issues || call.critical_issues.length === 0) && 
           (!call.moderate_issues || call.moderate_issues.length === 0) && (
            <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #4caf50' }}>
              <Typography variant="h6" gutterBottom color="success.main">
                <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                No Issues Detected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This call was handled exceptionally well with no significant issues identified.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Review Notes */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manual Review Notes
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        {call.reviewed_by && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last reviewed by {call.reviewed_by} on {new Date(call.reviewed_at!).toLocaleString()}
          </Typography>
        )}
        
        <TextField
          fullWidth
          multiline
          rows={4}
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
          placeholder="Add your review notes here..."
          sx={{ mb: 2 }}
        />
        
        <Button
          variant="contained"
          onClick={handleSaveReview}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Review'}
        </Button>
      </Paper>
    </Container>
  );
}

